/**
 * useChatHistory — Loads chat history from backend on mount.
 * Fetches last 100 messages from /api/user-chat/history/{agent_id}.
 * Supports search via ?search= query param.
 * Rule 94: Single responsibility — only handles history loading.
 */
import { useEffect, useRef } from 'react'
import { API_URL, apiFetch } from '../../../config/api'
import type { ChatMessage } from '../../../contexts/ChatContext'

interface DbMessage {
  id: number
  user_id: number
  agent_id: number
  message: string
  role: 'user' | 'assistant'
  created_at: string
  message_type?: string
}

export function useChatHistory(
  agentId: number | string | undefined,
  loadHistory: (messages: ChatMessage[]) => void,
  historyLoaded: boolean,
) {
  const lastLoadedAgentRef = useRef<number>(0)

  useEffect(() => {
    const numericId = agentId ? Number(agentId) : 0
    // Skip if: no agent, already loaded for this agent, or history already loaded
    if (!numericId || lastLoadedAgentRef.current === numericId || historyLoaded) return
    lastLoadedAgentRef.current = numericId

    const fetchHistory = async () => {
      try {
        console.log('[ChatHistory] Fetching for agent:', numericId)
        // G1 FIX: limit=100 (was 50)
        const res = await apiFetch(`${API_URL}/api/user-chat/history/${numericId}?limit=100`)
        if (!res.ok) {
          console.warn('[ChatHistory] Response not ok:', res.status)
          loadHistory([])
          return
        }
        const data = await res.json() as { ok?: boolean; messages?: DbMessage[] }
        console.log('[ChatHistory] Got:', data.ok, data.messages?.length ?? 0, 'messages')

        if (!data.ok || !data.messages?.length) {
          loadHistory([])
          return
        }

        // DB returns DESC order (newest first), reverse to chronological
        const sorted = [...data.messages].reverse()
        const mapped: ChatMessage[] = sorted.map(m => ({
          role: m.role,
          content: m.message,
          ts: m.created_at
            ? new Date(m.created_at).toLocaleTimeString()
            : new Date().toLocaleTimeString(),
          agentName: m.role === 'assistant' ? 'Agent' : undefined,
          status: 'done' as const,
          message_type: (m.message_type as ChatMessage['message_type']) ?? 'normal',
        }))

        loadHistory(mapped)
      } catch (e) {
        console.error('[ChatHistory] Load failed:', e)
        loadHistory([])
      }
    }

    void fetchHistory()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]) // Only re-run when agentId changes
}
